import React, { forwardRef, useRef, useEffect, Ref } from 'react';
import cx from 'classnames';

import { useCombinedRefs } from '@trqk/hooks';

import './checkbox.css';

interface Props {
    addClasses?: string;
    checked: boolean;
    color?: ColorFamily;
    disabled?: boolean;
    onChange?: OnChange;
    indeterminate?: boolean;
    inputId: string;
    label?: string;
    labelClasses?: string;
    readOnly?: boolean;
    size?: CheckboxSize;
    width?: number;
    boldWhenChecked?: boolean;
    /**
     * When true, the label will correctly wrap multiple lines. This was added after most of the
     * app was built, when it was discovered that the original CSS didn't correctly wrap lines. It
     * defaults to `false` to avoid breaking existing layouts.
     */
    wrapLabel?: boolean;
}

/**
 * Renders a checkbox that supports an 'indeterminate' state. This is useful when a checkbox
 * represents the state of several sub-checkboxes, and some of them are checked; this is usually
 * encountered in a table header used for row selection.
 * See "Indeterminate state checkboxes" in: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox
 * Styling based on: https://www.a11ywithlindsey.com/blog/create-custom-keyboard-accesible-checkboxes
 */
export const RenderCheckbox = (
    props: Props,
    ref: Ref<HTMLInputElement>,
): JSX.Element => {
    const {
        addClasses,
        boldWhenChecked = false,
        checked,
        color = 'secondary',
        disabled = false,
        onChange,
        indeterminate = false,
        inputId,
        label = '',
        labelClasses,
        readOnly,
        size = 'small',
        width,
        wrapLabel = false,
    } = props;
    const defaultRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, defaultRef);

    useEffect(() => {
        if (combinedRef?.current) {
            combinedRef.current.indeterminate = indeterminate;
        }
    }, [combinedRef, indeterminate]);

    return (
        <>
            <input
                id={inputId}
                ref={combinedRef}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                name={inputId}
                className={cx('checkbox', color, size, addClasses)}
                onChange={onChange}
                readOnly={readOnly}
            />
            <label
                htmlFor={inputId}
                className={cx({
                    disabled,
                    'wrap-label': wrapLabel,
                })}
            >
                <span
                    style={{ width }}
                    className={cx(labelClasses, {
                        'font-semibold': boldWhenChecked && checked,
                        truncate: !wrapLabel,
                        'wrap-label': wrapLabel,
                    })}
                >
                    {label}
                </span>
            </label>
        </>
    );
};

/**
 * Uses the RenderCheckbox function to forward a ref on to a rendered checkbox.
 */
export const Checkbox = forwardRef<HTMLInputElement, Props>(RenderCheckbox);
